<template>
  <v-app class="navrh-vyber-app">
    <v-app-bar
      app
      flat
      dark
      class="navrh-vyber-bar primaryGradientBackground"
    >
      <span class="navrh-vyber-brand font-weight-black">SEOWRITEE</span>
      <v-chip
        small
        class="ml-3 text-none"
        color="rgba(255,255,255,0.2)"
        text-color="white"
      >
        Finální doporučené řešení · prototyp
      </v-chip>
      <v-spacer />
      <v-btn
        depressed
        rounded
        color="ai"
        class="text-none lucy-bar-btn"
        disabled
      >
        <span class="lucy-bar-btn__mark">L</span>
        <span class="d-none d-sm-inline lucy-bar-btn__label">Zeptejte se AI Lucy</span>
      </v-btn>
    </v-app-bar>

    <v-main class="navrh-vyber-main">
      <v-container class="navrh-vyber-container py-6">
        <p class="navrh-eyebrow mb-1">
          SEOWRITEE — Easier SEO copywriting
        </p>
        <h1 class="text-h4 font-weight-bold mb-2">
          Výběr podkladů pro AI Lucy — jedna obrazovka, dvě rychlosti
        </h1>
        <p class="navrh-lead mb-4">
          Po analýze je článek připravený k jednomu kliknutí na <strong>Generovat SEO článek</strong>.
          Zkušený copywriter otevře stejné skupiny a upraví výběr — nic dalšího se neodhaluje jinde.
          Připomínky u bloků zůstávají jen v tomto prohlížeči (localStorage).
        </p>

        <NavrhBlockComment
          block-id="intro"
          title="Úvodní vysvětlení"
          :note="comments.intro"
          @update="setComment('intro', $event)"
        />
        <v-card
          outlined
          class="pa-4 mb-6 navrh-card"
        >
          <p class="mb-2">
            <strong>Rychlý copywriter:</strong>
            po analýze vidí krátké shrnutí („AI Lucy napíše článek…“), předvybraných je typicky ~10 odstavců
            (až 6 souvisejících témat jako H2, až 4 nejlepší otázky jako FAQ), 2–3 citace a fakta z vámi zvolených
            AI chatů (AI Overview, ChatGPT, Gemini, Perplexity) jsou už zapracovaná v textu — bez checkboxů u každé věty.
            Hlavní klíčové slovo je téma článku (H1) a nezabírá slot.
          </p>
          <p class="mb-2">
            <strong>Zkušený SEO copywriter:</strong>
            stejná obrazovka — rozbalí skupiny, vypne otázku, přepne téma na
            <strong>Doplnit do textu</strong> (počítadlo klesne), přidá long-tail jako vlastní odstavec,
            nebo vyloučí fakta z celého chatu.
          </p>
          <p class="mb-0 text--secondary">
            <strong>Proč limit 20:</strong>
            do „Odstavce X / 20“ počítáme jen bloky <strong>Vlastní odstavec (H2)</strong> a <strong>Otázka (FAQ)</strong>.
            <strong>Doplnit do textu</strong> a outbound citace E-E-A-T limit nezvyšují — AI Lucy je zapracuje do existujících částí.
          </p>
        </v-card>

        <div class="d-flex flex-wrap align-center mb-4 navrh-toolbar">
          <span class="caption font-weight-bold text-uppercase mr-2">Scénář:</span>
          <v-btn-toggle
            v-model="scenario"
            mandatory
            dense
            class="navrh-seg mr-4"
            @change="onScenarioChange"
          >
            <v-btn
              small
              value="quick"
              class="text-none"
            >
              Rychlý článek
            </v-btn>
            <v-btn
              small
              value="customize"
              class="text-none"
            >
              Chci si článek přizpůsobit
            </v-btn>
            <v-btn
              small
              value="over-limit"
              class="text-none"
            >
              Předvybráno nad limit
            </v-btn>
          </v-btn-toggle>
          <v-btn-toggle
            v-model="viewport"
            mandatory
            dense
            class="navrh-seg"
          >
            <v-btn
              small
              value="desktop"
              class="text-none"
            >
              Desktop
            </v-btn>
            <v-btn
              small
              value="mobile"
              class="text-none"
            >
              Mobil
            </v-btn>
          </v-btn-toggle>
        </div>

        <NavrhBlockComment
          block-id="scenare"
          title="Demo scénáře"
          :note="comments.scenare"
          @update="setComment('scenare', $event)"
        />

        <v-alert
          v-if="scenarioNotice"
          :type="scenarioNotice.type"
          dense
          text
          class="mb-4 text-body-2"
        >
          {{ scenarioNotice.text }}
        </v-alert>

        <div
          class="navrh-mock-wrap"
          :class="{'navrh-mock-wrap--mobile': viewport === 'mobile'}"
        >
          <div class="navrh-app-shell">
            <div class="navrh-app-bar-inner">
              <span class="font-weight-black">SEOWRITEE</span>
              <span class="navrh-bar-pill">Návrh UX</span>
            </div>
            <div class="navrh-workspace">
              <div class="text-center mb-3">
                <div class="text-h6">
                  Výběr klíčových slov pro
                  <strong>spořicí účet</strong>
                </div>
                <p class="caption text--secondary mb-2">
                  Během několika desítek sekund vám AI Lucy napíše celý článek.
                </p>
                <div class="d-flex flex-wrap justify-center">
                  <v-chip
                    small
                    class="ma-1 text-none"
                    color="primary"
                    outlined
                  >
                    Standardní článek (~1200 slov)
                  </v-chip>
                  <v-chip
                    small
                    class="ma-1 text-none"
                    outlined
                  >
                    moneta spořící účet
                  </v-chip>
                </div>
              </div>

              <v-card
                v-if="showFastSummary"
                outlined
                class="pa-3 mb-3 navrh-fast-summary"
              >
                <div class="d-flex align-start">
                  <span class="navrh-lucy-avatar">L</span>
                  <div>
                    <strong>{{ fastSummaryTitle }}</strong>
                    <p class="caption text--secondary mb-0 mt-1">
                      {{ fastSummaryDetail }}
                    </p>
                  </div>
                </div>
              </v-card>

              <v-card
                v-if="showQuickFork"
                outlined
                class="pa-4 mb-4 navrh-quick-fork"
              >
                <h3 class="text-subtitle-1 font-weight-bold mb-1">
                  Jak chcete článek napsat?
                </h3>
                <p class="caption text--secondary mb-4">
                  Obě cesty používají stejnou předvolbu z analýzy — liší se jen tím, jestli něco upravíte před psaním.
                </p>
                <v-row dense>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <div class="navrh-fork-col">
                      <NavrhControlTip
                        :text="forkTipImmediate"
                        block
                      >
                        <v-btn
                          color="accent"
                          depressed
                          block
                          large
                          class="text-none"
                          @click="onGenerovatHned"
                        >
                          <v-icon
                            left
                            small
                          >
                            flash_on
                          </v-icon>
                          Generovat hned
                        </v-btn>
                      </NavrhControlTip>
                      <p class="caption text-center mt-2 mb-0 navrh-fork-line">
                        AI Lucy napíše hned z připraveného výběru — nic nemusíte rozbalovat.
                      </p>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <div class="navrh-fork-col">
                      <NavrhControlTip
                        :text="forkTipCustomize"
                        block
                      >
                        <v-btn
                          outlined
                          block
                          large
                          class="text-none"
                          color="primary"
                          @click="onUpravitPredGenerovanim"
                        >
                          <v-icon
                            left
                            small
                          >
                            tune
                          </v-icon>
                          Upravit před generováním
                        </v-btn>
                      </NavrhControlTip>
                      <p class="caption text-center mt-2 mb-0 navrh-fork-line">
                        Rozbalíte skupiny, vypnete řádky a zvolíte Vlastní odstavec / Otázka / Doplnit do textu.
                      </p>
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <v-expand-transition>
                <v-card
                  v-if="immediatePreview"
                  outlined
                  class="pa-4 mb-4 navrh-immediate-preview"
                  color="green lighten-5"
                >
                  <div class="d-flex align-start mb-2">
                    <v-icon
                      color="success"
                      class="mr-2"
                    >
                      check_circle
                    </v-icon>
                    <div>
                      <strong>Generovat hned — co by AI Lucy napsala</strong>
                      <p class="caption text--secondary mb-0 mt-1">
                        Prototyp nevolá OpenAI. V produkci by se teď spustilo psaní článku z tohoto výběru.
                      </p>
                    </div>
                  </div>
                  <ul class="navrh-preview-list pl-4 mb-2">
                    <li
                      v-for="line in immediatePreview.lines"
                      :key="line"
                    >
                      {{ line }}
                    </li>
                  </ul>
                  <p class="caption mb-0 text--secondary">
                    Skupiny můžete rozbalit a výběr změnit — pak použijte
                    <strong>Generovat SEO článek</strong>
                    v záhlaví.
                  </p>
                </v-card>
              </v-expand-transition>

              <div class="navrh-brief-strip mb-3">
                <div class="d-flex align-start flex-wrap">
                  <span class="navrh-lucy-avatar">L</span>
                  <div class="flex-grow-1 mb-2 mb-sm-0">
                    <strong>Zadání z odpovědí AI chatů</strong>
                    <div class="caption text--secondary">
                      Tvar odpovědi a živé odpovědi AI chatů pro citovatelný článek.
                    </div>
                  </div>
                  <v-tooltip
                    top
                    :disabled="!generateDisabledReason"
                  >
                    <template #activator="{ on }">
                      <span v-on="on">
                        <v-btn
                          v-show="showBriefGenerate"
                          color="accent"
                          depressed
                          class="text-none navrh-generovat"
                          :disabled="!!generateDisabledReason"
                          @click="onGenerovatClick"
                        >
                          <v-icon
                            left
                            small
                          >
                            send
                          </v-icon>
                          Generovat SEO článek
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ generateDisabledReason }}</span>
                  </v-tooltip>
                </div>
              </div>

              <NavrhBlockComment
                block-id="generate"
                title="Tlačítko Generovat"
                :note="comments.generate"
                compact
                @update="setComment('generate', $event)"
              />

              <div class="navrh-counter-bar mb-2">
                <div>
                  <strong>Odstavce {{ slotCount }} / {{ maxSlots }}</strong>
                  <NavrhControlTip
                    class="ml-1 d-inline-flex"
                    :text="tipLimit20"
                    icon-only
                  />
                  <span class="caption text--secondary ml-2 d-none d-md-inline">
                    počítají se jen
                    <NavrhAbbrTip
                      label="Vlastní odstavec"
                      :text="tipIntentSection"
                      inline-label
                    />
                    (<NavrhAbbrTip
                      label="H2"
                      :text="tipH2"
                      inline-label
                    />)
                    a
                    <NavrhAbbrTip
                      label="Otázka"
                      :text="tipIntentFaq"
                      inline-label
                    />
                    (<NavrhAbbrTip
                      label="FAQ"
                      :text="tipFaq"
                      inline-label
                    />)
                  </span>
                </div>
                <v-chip
                  small
                  :color="slotCount > maxSlots ? 'warning' : slotCount === 0 ? 'grey' : 'accent'"
                  class="font-weight-bold"
                >
                  {{ slotCount }} / {{ maxSlots }}
                </v-chip>
              </div>
              <p
                class="caption mb-4"
                :class="limitMessageClass"
              >
                {{ limitMessage }}
              </p>

              <NavrhBlockComment
                block-id="counter"
                title="Počítadlo a legenda"
                :note="comments.counter"
                compact
                @update="setComment('counter', $event)"
              />

              <NavrhBlockComment
                block-id="skupiny"
                title="Skupiny a řádky"
                :note="comments.skupiny"
                compact
                @update="setComment('skupiny', $event)"
              />

              <KeywordWorkspaceGroup
                v-for="group in visibleGroups"
                :key="group.id"
                :group-id="group.id"
                :title="group.title"
                :subtitle="group.subtitle"
                :count-label="groupCountLabel(group)"
                blue
                :open="groupOpen[group.id]"
                @toggle="toggleGroup(group.id)"
              >
                <template v-if="group.id === 'g-facts'">
                  <p class="caption px-3 pt-2 pb-1 text--secondary">
                    Fakta z chatů, které jste už vybrali v analýze — AI Lucy je zapracuje do příslušných částí.
                    Nevybíráte jednotlivé věty; můžete vyloučit celý chat.
                  </p>
                  <div
                    v-for="chat in factChats"
                    :key="chat.id"
                    class="navrh-fact-chat"
                  >
                    <div class="navrh-fact-chat__head">
                      <span class="navrh-src navrh-src--chat">{{ chat.label }}</span>
                      <v-switch
                        :input-value="factsChatOn(chat.id)"
                        dense
                        hide-details
                        class="mt-0 pt-0 navrh-fact-switch"
                        :label="factsChatOn(chat.id) ? 'Zapracovat' : 'Vyloučeno'"
                        @change="setFactsChat(chat.id, $event)"
                      />
                    </div>
                    <ul class="navrh-fact-list">
                      <li
                        v-for="line in chat.lines"
                        :key="line"
                      >
                        {{ line }}
                      </li>
                    </ul>
                  </div>
                </template>
                <template v-else>
                  <div class="navrh-col-head d-none d-sm-grid">
                    <span>Fráze</span>
                    <span class="text-right d-inline-flex align-center justify-end">
                      <NavrhAbbrTip
                        label="Pro AI Lucy"
                        :text="tipProAiLucy"
                        inline-label
                      />
                    </span>
                  </div>
                  <p
                    v-if="groupSubtitleTips(group.id).length"
                    class="caption px-3 pt-2 pb-0 mb-0 text--secondary navrh-group-subtips"
                  >
                    <span
                      v-for="(tip, idx) in groupSubtitleTips(group.id)"
                      :key="group.id + '-' + tip.label"
                    >
                      <NavrhAbbrTip
                        :label="tip.label"
                        :text="tip.text"
                      />
                      <span
                        v-if="idx < groupSubtitleTips(group.id).length - 1"
                        class="mx-1"
                      >·</span>
                    </span>
                  </p>
                  <div class="navrh-scroll-10">
                    <div
                      v-for="row in group.items"
                      :key="row.id"
                      class="navrh-row"
                      :class="{'navrh-row--main': row.isMain}"
                    >
                      <div class="navrh-row__mid">
                        <div class="navrh-row__phrase">
                          {{ row.phrase }}
                          <v-icon
                            v-if="row.isMain"
                            x-small
                            class="ml-1"
                            color="white"
                          >
                            check_circle
                          </v-icon>
                        </div>
                        <div class="navrh-row__meta">
                          <span
                            class="navrh-src"
                            :class="'navrh-src--' + row.sourceClass"
                          >
                            <NavrhAbbrTip
                              v-if="sourceTip(row.sourceLabel)"
                              :label="row.sourceLabel"
                              :text="sourceTip(row.sourceLabel)"
                            />
                            <template v-else>{{ row.sourceLabel }}</template>
                          </span>
                          <span
                            v-if="row.volume"
                            class="navrh-vol"
                          >{{ row.volume }}</span>
                          <span
                            v-if="row.isMain"
                            class="navrh-slot-tag navrh-slot-tag--free"
                          >
                            <NavrhAbbrTip
                              label="H1"
                              :text="tipH1"
                            />
                            téma · nepočítá se
                          </span>
                          <span
                            v-else-if="rowSlotTag(row)"
                            class="navrh-slot-tag"
                          >
                            <template v-if="intentForRow(row.id) === 'section'">
                              1 odstavec (<NavrhAbbrTip
                                label="H2"
                                :text="tipH2"
                              />)
                            </template>
                            <template v-else>
                              1 odstavec (<NavrhAbbrTip
                                label="FAQ"
                                :text="tipFaq"
                              />)
                            </template>
                          </span>
                          <span
                            v-else-if="isLucyOn(row.id)"
                            class="navrh-slot-tag navrh-slot-tag--free"
                          >nelimituje 20</span>
                        </div>
                      </div>
                      <div class="navrh-row__controls">
                        <template v-if="row.isMain">
                          <v-chip
                            x-small
                            class="text-none navrh-main-chip"
                            color="grey darken-3"
                            text-color="white"
                          >
                            Vždy v článku
                          </v-chip>
                        </template>
                        <template v-else>
                          <NavrhControlTip :text="lucyTooltip(row)">
                            <v-btn
                              depressed
                              class="text-none navrh-lucy-btn"
                              :class="isLucyOn(row.id) ? 'navrh-lucy-btn--on' : 'navrh-lucy-btn--off'"
                              :aria-pressed="isLucyOn(row.id) ? 'true' : 'false'"
                              @click="toggleLucy(row)"
                            >
                              <v-icon
                                left
                                x-small
                              >
                                {{ isLucyOn(row.id) ? 'check' : 'add' }}
                              </v-icon>
                              Pro AI Lucy
                            </v-btn>
                          </NavrhControlTip>
                          <div
                            v-if="isLucyOn(row.id) && rowAllowsIntent(row)"
                            class="navrh-intent-toggle mt-1 d-flex flex-wrap"
                          >
                            <NavrhControlTip
                              v-for="opt in intentOptionsForRow(row)"
                              :key="opt.value"
                              :text="intentTip(opt.value)"
                            >
                              <v-btn
                                x-small
                                class="text-none"
                                :depressed="intentForRow(row.id) === opt.value"
                                :outlined="intentForRow(row.id) !== opt.value"
                                :color="intentForRow(row.id) === opt.value ? 'primary' : undefined"
                                @click="setIntent(row.id, opt.value)"
                              >
                                {{ opt.label }}
                              </v-btn>
                            </NavrhControlTip>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </KeywordWorkspaceGroup>

              <v-expand-transition>
                <v-card
                  v-if="lastGenerateFlash"
                  outlined
                  class="mt-4 pa-3 navrh-flash"
                  color="green lighten-5"
                >
                  {{ lastGenerateFlash }}
                </v-card>
              </v-expand-transition>
            </div>
          </div>
        </div>

        <NavrhBlockComment
          block-id="ovladani"
          title="Jednotné ovládání"
          :note="comments.ovladani"
          @update="setComment('ovladani', $event)"
        />

        <v-expansion-panels
          flat
          class="mt-6 mb-4"
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              Dříve / nyní (sekundární srovnání)
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <NavrhBlockComment
                block-id="srovnani"
                title="Srovnání"
                :note="comments.srovnani"
                compact
                @update="setComment('srovnani', $event)"
              />
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Skupina</th>
                    <th>Dnes</th>
                    <th>Návrh</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in compareRows"
                    :key="row.group"
                  >
                    <td>{{ row.group }}</td>
                    <td class="text--secondary">
                      {{ row.today }}
                    </td>
                    <td>{{ row.proposal }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-card
          outlined
          class="pa-4 mb-6"
        >
          <h2 class="text-h6 font-weight-bold mb-2">
            Shrnutí rozhodnutí (finální návrh)
          </h2>
          <ul class="navrh-decisions pl-4 mb-0">
            <li>
              Jedna obrazovka bez přepínače režimu: rychlý uživatel generuje z předvolby, zkušený uživatel upravuje stejné řádky.
            </li>
            <li>
              Předvolba kvalitně vybírá ~10 odstavců (H2 + FAQ), citace a fakta mimo limit; long-tail a LSI nejsou hromadně zapnuté.
            </li>
            <li>
              Ovládání: přepínač <strong>Pro AI Lucy</strong> + volba <strong>Vlastní odstavec</strong> / <strong>Otázka</strong> / <strong>Doplnit do textu</strong> — bez levých checkboxů a bez konkurenčních tlačítek.
            </li>
          </ul>
        </v-card>

        <v-card
          outlined
          class="pa-4"
        >
          <div class="d-flex flex-wrap align-center mb-2">
            <h2 class="text-h6 font-weight-bold flex-grow-1">
              Souhrn připomínek
            </h2>
            <v-btn
              small
              depressed
              color="primary"
              class="text-none"
              @click="copyCommentsSummary"
            >
              Kopírovat do schránky
            </v-btn>
          </div>
          <pre class="navrh-summary">{{ commentsSummary }}</pre>
        </v-card>
      </v-container>
    </v-main>

    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      bottom
    >
      {{ snackbarText }}
    </v-snackbar>
  </v-app>
</template>

<script lang="ts">
    import Vue from 'vue';
    import KeywordWorkspaceGroup from '@/components/keywords/KeywordWorkspaceGroup.vue';
    import NavrhAbbrTip from '@/components/navrh/NavrhAbbrTip.vue';
    import NavrhControlTip from '@/components/navrh/NavrhControlTip.vue';
    import NavrhBlockComment from '@/components/navrh/NavrhBlockComment.vue';

    const STORAGE_KEY = 'seowritee-navrh-vyber-pripominky-v1';
    const MAX_SLOTS = 20;

    type RowKind = 'topic' | 'question' | 'citation' | 'variant';
    type Intent = 'section' | 'faq' | 'weave';

    type DemoRow = {
        id: string;
        groupId: string;
        phrase: string;
        sourceClass: string;
        sourceLabel: string;
        volume?: string;
        kind: RowKind;
        isMain?: boolean;
    };

    type DemoGroup = {
        id: string;
        title: string;
        subtitle: string;
        items: DemoRow[];
    };

    type FactChat = {
        id: string;
        label: string;
        lines: string[];
    };

    const FACT_CHATS: FactChat[] = [
        {
            id: 'chatgpt',
            label: 'ChatGPT',
            lines: [
                'Úrok kolem 4–4,5 % p.a. bez výpovědní lhůty',
                'Srovnání sazeb se liší podle výše vkladu',
            ],
        },
        {
            id: 'perplexity',
            label: 'Perplexity',
            lines: [
                'Vyšší sazby často vyžadují splnění podmínek (aktivní účet, karta)',
            ],
        },
        {
            id: 'gemini',
            label: 'Gemini',
            lines: [
                'Garance úroku je většinou časově omezená',
            ],
        },
        {
            id: 'aio',
            label: 'AI Overview',
            lines: [
                'Důraz na pojištění vkladů do 100 000 EUR',
            ],
        },
    ];

    const DEMO_GROUPS: DemoGroup[] = [
        {
            id: 'g-related',
            title: 'Klíčová slova – související témata',
            subtitle: 'Data z analýzy + People also search',
            items: [
                {id: 'r-main', groupId: 'g-related', phrase: 'spořicí účet', sourceClass: 'top', sourceLabel: 'hlavní', kind: 'topic', isMain: true},
                {id: 'r-1', groupId: 'g-related', phrase: 'spořicí účet úrok', sourceClass: 'top', sourceLabel: 'TOP', volume: '12 400', kind: 'topic'},
                {id: 'r-2', groupId: 'g-related', phrase: 'nejlepší spořicí účet', sourceClass: 'pas', sourceLabel: 'PAS', volume: '8 900', kind: 'topic'},
                {id: 'r-3', groupId: 'g-related', phrase: 'spořicí účet bez podmínek', sourceClass: 'top', sourceLabel: 'TOP', volume: '3 200', kind: 'topic'},
                {id: 'r-4', groupId: 'g-related', phrase: 'úročení spořicího účtu', sourceClass: 'labs', sourceLabel: 'Labs', volume: '1 100', kind: 'topic'},
                {id: 'r-5', groupId: 'g-related', phrase: 'spořicí účet pro děti', sourceClass: 'top', sourceLabel: 'TOP', volume: '720', kind: 'topic'},
                {id: 'r-6', groupId: 'g-related', phrase: 'srovnání spořicích účtů', sourceClass: 'pas', sourceLabel: 'PAS', volume: '2 400', kind: 'topic'},
                {id: 'r-7', groupId: 'g-related', phrase: 'spořicí účet online založení', sourceClass: 'top', sourceLabel: 'TOP', volume: '980', kind: 'topic'},
                {id: 'r-8', groupId: 'g-related', phrase: 'bonus za založení účtu', sourceClass: 'labs', sourceLabel: 'Labs', volume: '540', kind: 'topic'},
                {id: 'r-9', groupId: 'g-related', phrase: 'spořicí účet pro firmy', sourceClass: 'top', sourceLabel: 'TOP', volume: '310', kind: 'topic'},
                {id: 'r-10', groupId: 'g-related', phrase: 'krátkodobé spoření', sourceClass: 'pas', sourceLabel: 'PAS', volume: '890', kind: 'topic'},
                {id: 'r-11', groupId: 'g-related', phrase: 'spořicí účet poplatky', sourceClass: 'top', sourceLabel: 'TOP', volume: '450', kind: 'topic'},
            ],
        },
        {
            id: 'g-questions',
            title: 'Klíčová slova – otázky',
            subtitle: 'PAA, AI chat, autocomplete-otázky — odděleně od LSI',
            items: [
                {id: 'q-1', groupId: 'g-questions', phrase: 'Jaký spořicí účet má nejvyšší úrok?', sourceClass: 'paa', sourceLabel: 'PAA', volume: 'AI 82', kind: 'question'},
                {id: 'q-2', groupId: 'g-questions', phrase: 'Kolik peněz můžu mít na spořicím účtu?', sourceClass: 'chat', sourceLabel: 'AI chat', kind: 'question'},
                {id: 'q-3', groupId: 'g-questions', phrase: 'Vyplatí se spořicí účet v roce 2026?', sourceClass: 'paa', sourceLabel: 'PAA', kind: 'question'},
                {id: 'q-4', groupId: 'g-questions', phrase: 'Jak často banka mění úrok?', sourceClass: 'ac', sourceLabel: 'Autocomplete', kind: 'question'},
                {id: 'q-5', groupId: 'g-questions', phrase: 'Musím platit daň z úroků?', sourceClass: 'paa', sourceLabel: 'PAA', kind: 'question'},
                {id: 'q-6', groupId: 'g-questions', phrase: 'Co je garance úroku?', sourceClass: 'chat', sourceLabel: 'Gemini', kind: 'question'},
                {id: 'q-7', groupId: 'g-questions', phrase: 'Spořicí účet vs termínovaný vklad?', sourceClass: 'paa', sourceLabel: 'PAA', kind: 'question'},
                {id: 'q-8', groupId: 'g-questions', phrase: 'Kde je spořicí účet nejbezpečnější?', sourceClass: 'chat', sourceLabel: 'ChatGPT', kind: 'question'},
                {id: 'q-9', groupId: 'g-questions', phrase: 'Jak založit spořicí účet online?', sourceClass: 'paa', sourceLabel: 'PAA', kind: 'question'},
            ],
        },
        {
            id: 'g-lsi',
            title: 'Varianty a LSI slova',
            subtitle: 'Slovní varianty — výchozí Doplnit do textu, ne nová sekce',
            items: [
                {id: 'l-1', groupId: 'g-lsi', phrase: 'úroková sazba p.a.', sourceClass: 'lsi', sourceLabel: 'LSI', kind: 'variant'},
                {id: 'l-2', groupId: 'g-lsi', phrase: 'výpovědní lhůta', sourceClass: 'lsi', sourceLabel: 'LSI', kind: 'variant'},
                {id: 'l-3', groupId: 'g-lsi', phrase: 'pojištění vkladů', sourceClass: 'lsi', sourceLabel: 'LSI', kind: 'variant'},
            ],
        },
        {
            id: 'g-long',
            title: 'Long-tail fráze',
            subtitle: 'Delší dotazy z Labs — v předvolbě vypnuto',
            items: [
                {id: 'lt-1', groupId: 'g-long', phrase: 'spořicí účet s nejvyšším úrokem bez podmínek 2026', sourceClass: 'labs', sourceLabel: 'Labs', kind: 'variant'},
                {id: 'lt-2', groupId: 'g-long', phrase: 'kam uložit peníze na krátkodobé spoření', sourceClass: 'labs', sourceLabel: 'Labs', kind: 'variant'},
            ],
        },
        {
            id: 'g-cite',
            title: 'Zdroje, které AI cituje',
            subtitle: 'Outbound citace E-E-A-T — nepočítají se do 20 odstavců',
            items: [
                {id: 'c-1', groupId: 'g-cite', phrase: 'Spořicí účty srovnání 2026 – Ušetřeno.cz', sourceClass: 'chat', sourceLabel: 'z AI chatu', kind: 'citation'},
                {id: 'c-2', groupId: 'g-cite', phrase: 'Spořící účty: Nenechte se zlákat jen vysokým úrokem', sourceClass: 'chat', sourceLabel: 'AI Overview', kind: 'citation'},
                {id: 'c-3', groupId: 'g-cite', phrase: 'Pojištění vkladů – ČNB', sourceClass: 'chat', sourceLabel: 'oficiální zdroj', kind: 'citation'},
            ],
        },
        {
            id: 'g-facts',
            title: 'Fakta z AI odpovědí',
            subtitle: 'Už zapracovaný kontext — ne checkboxy u vět',
            items: [],
        },
        {
            id: 'g-ac',
            title: 'Autocomplete fráze',
            subtitle: 'Návrhy z vyhledávání (ne otázky) — v předvolbě vypnuto',
            items: [
                {id: 'ac-1', groupId: 'g-ac', phrase: 'spořicí účet air bank', sourceClass: 'ac', sourceLabel: 'AC', kind: 'variant'},
                {id: 'ac-2', groupId: 'g-ac', phrase: 'spořicí účet česká spořitelna', sourceClass: 'ac', sourceLabel: 'AC', kind: 'variant'},
            ],
        },
    ];

    const ALL_ROWS: DemoRow[] = DEMO_GROUPS.flatMap((g) => g.items);

    const INTENT_LABELS: Record<Intent, string> = {
        section: 'Vlastní odstavec',
        faq: 'Otázka',
        weave: 'Doplnit do textu',
    };

    const QUICK_TOPICS = ['r-1', 'r-2', 'r-3', 'r-4', 'r-5', 'r-6'];
    const QUICK_QUESTIONS = ['q-1', 'q-2', 'q-3', 'q-5'];
    const QUICK_CITATIONS = ['c-1', 'c-2', 'c-3'];

    const SOURCE_TIPS: Record<string, string> = {
        TOP: 'Stránky a klíčová slova z prvních organických výsledků Google pro dané téma — stejný zdroj jako inspirace z TOP obsahu.',
        PAS: 'Návrhy „Lidé také hledají“ u výsledků Google: související témata, která lidé často hledají po stejném dotazu.',
        PAA: 'Otázky, které Google ukazuje u výsledků hledání (People Also Ask).',
        LSI: 'Slova a sousloví, která s tématem souvisí a patří do přirozeného textu — entity a varianty, ne samostatné otázky.',
        Labs: 'Delší fráze a návrhy z nástroje Keyword Labs (long-tail dotazy z analýzy).',
        AC: 'Fráze z dopisování ve vyhledávacím poli Google (autocomplete), které nejsou otázky.',
        Autocomplete: 'Návrh z dopisování ve vyhledávání — v této skupině jen pokud jde o otázku.',
        'AI chat': 'Otázka nebo téma z odpovědi vybraného AI chatu v analýze.',
        ChatGPT: 'Otázka nebo kontext z odpovědi ChatGPT, kterou jste zvolili v analýze.',
        Gemini: 'Otázka nebo kontext z odpovědi Gemini, kterou jste zvolili v analýze.',
        Perplexity: 'Otázka nebo kontext z odpovědi Perplexity, kterou jste zvolili v analýze.',
        'AI Overview': 'Kontext nebo citace z Google AI Overview podle vaší analýzy.',
        'z AI chatu': 'Outbound odkaz, který AI chaty použily jako zdroj — AI Lucy ho může zmínit v textu.',
        'oficiální zdroj': 'Ověřitelný autoritativní web vhodný jako outbound citace.',
        hlavní: 'Hlavní klíčové slovo analýzy — vždy téma článku v H1, nepočítá se do 20 odstavců.',
    };

    export default Vue.extend({
        name: 'NavrhVyberView',
        components: {
            KeywordWorkspaceGroup,
            NavrhBlockComment,
            NavrhAbbrTip,
            NavrhControlTip,
        },
        data() {
            return {
                maxSlots: MAX_SLOTS,
                scenario: 'quick' as 'quick' | 'customize' | 'over-limit',
                quickWritePath: null as null | 'immediate' | 'customize-first',
                immediatePreview: null as null | {lines: string[]},
                viewport: 'desktop' as 'desktop' | 'mobile',
                tipH1: 'Hlavní nadpis článku — téma z analýzy. Nezabírá slot mezi 20 odstavci.',
                tipH2: 'Podnadpis vlastní sekce článku. Každý zapnutý Vlastní odstavec zabírá jeden slot.',
                tipFaq: 'Blok otázka a odpověď ve článku. Každá zapnutá Otázka zabírá jeden slot.',
                tipProAiLucy: 'Zapne nebo vypne téma pro psaní. Vypnutí vždy funguje, i nad limitem 20 odstavců.',
                tipLimit20: 'Maximálně 20 vlastních sekcí (H2) a FAQ dohromady. Doplnit do textu, citace a fakta z chatů se nepočítají.',
                tipIntentSection: 'AI Lucy napíše samostatný odstavec s podnadpisem H2 k danému tématu.',
                tipIntentFaq: 'AI Lucy zpracuje frázi jako otázku a odpověď ve FAQ.',
                tipIntentWeave: 'AI Lucy zapracuje výraz do existujících částí bez nového podnadpisu — nepočítá se do 20.',
                tipEeat: 'Outbound citace zvyšují důvěryhodnost (E-E-A-T): AI Lucy zmíní zdroj v textu, ne jako seznam odkazů.',
                tipLongTail: 'Delší konkrétní dotazy z analýzy — v předvolbě jsou vypnuté, zapnete je jako vlastní odstavec.',
                tipSerp: 'Stránka výsledků vyhledávání (SERP) — kontext pro objem hledání a návrhy témat.',
                forkTipImmediate: 'AI Lucy napíše teď z připraveného výběru: cca 6 vlastních sekcí, až 4 otázky, 2–3 citace a fakta z chatů, které jste už zvolili v analýze. Nemusíte nic rozbalovat.',
                forkTipCustomize: 'Rozbalí skupiny jako u zkušeného copywritera: vypnete řádky, přidáte long-tail a u každého zvolíte Vlastní odstavec, Otázku nebo Doplnit do textu (max. 20 odstavců).',
                lucySelected: {} as Record<string, boolean>,
                lucyIntent: {} as Record<string, Intent>,
                factsChatEnabled: {} as Record<string, boolean>,
                groupOpen: {
                    'g-related': false,
                    'g-questions': false,
                    'g-lsi': false,
                    'g-long': false,
                    'g-cite': false,
                    'g-facts': false,
                    'g-ac': false,
                } as Record<string, boolean>,
                comments: {} as Record<string, string>,
                lastGenerateFlash: '',
                snackbar: false,
                snackbarText: '',
                compareRows: [
                    {group: 'Související / otázky', today: 'Checkbox vlevo „Pro AI Lucy“', proposal: 'Přepínač vpravo + volba odstavec / FAQ / doplnit'},
                    {group: 'Citované zdroje', today: 'Samostatná pilulka', proposal: 'Stejný přepínač, výchozí Doplnit do textu (citace)'},
                    {group: 'Předvolba', today: 'Vše related + vše otázky bez stropu', proposal: '~10 kvalitních odstavců + citace a fakta mimo limit'},
                    {group: 'Generovat', today: 'Skryté po prvním textu / u analýzy URL', proposal: 'Vždy viditelné, vypnuté jen s důvodem'},
                    {group: 'Limit', today: 'Blokuje tlačítko nad 20', proposal: 'Vypnutí vždy jde; nový H2/FAQ jen pod 20'},
                ],
            };
        },
        computed: {
            visibleGroups(): DemoGroup[] {
                return DEMO_GROUPS;
            },
            factChats(): FactChat[] {
                return FACT_CHATS;
            },
            showFastSummary(): boolean {
                return this.scenario === 'quick';
            },
            showQuickFork(): boolean {
                return this.scenario === 'quick' && this.quickWritePath === null;
            },
            showBriefGenerate(): boolean {
                if (this.scenario !== 'quick') {
                    return true;
                }
                return this.quickWritePath === 'customize-first' || this.quickWritePath === 'immediate';
            },
            fastSummaryTitle(): string {
                const n = this.slotCount;
                return `AI Lucy napíše článek z ${n} vybraných odstavců`;
            },
            fastSummaryDetail(): string {
                const citeN = QUICK_CITATIONS.filter((id) => this.lucySelected[id]).length;
                const chats = FACT_CHATS.filter((c) => this.factsChatOn(c.id)).map((c) => c.label).join(', ');
                return `Téma článku: „spořicí účet“ (H1). Zapnuto ${citeN} citací a fakta z: ${chats || '—'}. Skupiny můžete rozbalit a upravit výběr.`;
            },
            slotCount(): number {
                return ALL_ROWS.filter((row) => this.rowCountsAsSlot(row)).length;
            },
            generateDisabledReason(): string {
                if (this.slotCount === 0) {
                    return 'Vyberte alespoň jeden vlastní odstavec (H2) nebo otázku (FAQ) — max. 20 odstavců.';
                }
                if (this.slotCount > this.maxSlots) {
                    return `Máte ${this.slotCount} odstavců — vypněte jiný odstavec, nebo z něj udělejte „Doplnit do textu“.`;
                }
                return '';
            },
            limitMessage(): string {
                if (this.slotCount === 0) {
                    return 'Žádný odstavec není zapnutý — Generovat je vidět, ale vypnuté.';
                }
                if (this.slotCount > this.maxSlots) {
                    return 'Překročen limit 20: vypněte jiný odstavec, nebo z něj udělejte „Doplnit do textu“. Vypínání vždy funguje.';
                }
                if (this.slotCount === this.maxSlots) {
                    return 'Limit 20 je plný — další vlastní odstavec nebo otázku zapnete jen po úpravě jiného řádku.';
                }
                return `Do limitu zbývá ${this.maxSlots - this.slotCount} odstavců (H2 nebo FAQ). Doplnit do textu a citace se nepočítají.`;
            },
            limitMessageClass(): string {
                if (this.slotCount > this.maxSlots) {
                    return 'warning--text';
                }
                if (this.slotCount === this.maxSlots) {
                    return 'primary--text';
                }
                return 'text--secondary';
            },
            scenarioNotice(): {type: string; text: string} | null {
                if (this.scenario === 'quick' && this.quickWritePath === null) {
                    return {
                        type: 'success',
                        text: 'Scénář „Rychlý článek“: zvolte Generovat hned nebo Upravit před generováním — obě cesty používají stejnou předvolbu.',
                    };
                }
                if (this.scenario === 'quick' && this.slotCount <= this.maxSlots && this.slotCount > 0) {
                    return {
                        type: 'success',
                        text: 'Scénář „Rychlý článek“: předvolba ~10 odstavců. Po úpravě použijte Generovat SEO článek, nebo se vraťte k volbě výše (přepněte scénář).',
                    };
                }
                if (this.scenario === 'customize') {
                    return {
                        type: 'info',
                        text: 'Scénář „Chci si článek přizpůsobit“: stejná data, rozbalené skupiny — zkuste přepnout téma na Doplnit do textu, přidat long-tail nebo vypnout otázku.',
                    };
                }
                if (this.scenario === 'over-limit') {
                    return {
                        type: 'warning',
                        text: `Scénář „Předvybráno nad limit“: simuluje starou předvolbu „všechno zapnout“ (${this.slotCount} odstavců). Upravte výběr pod 20 — Generovat se pak zapne.`,
                    };
                }
                return null;
            },
            commentsSummary(): string {
                const lines = ['Souhrn připomínek — návrh výběru podkladů (SEOWRITEE)', ''];
                const labels: Record<string, string> = {
                    intro: 'Úvod',
                    ovladani: 'Jednotné ovládání',
                    scenare: 'Scénáře',
                    counter: 'Počítadlo',
                    generate: 'Generovat',
                    skupiny: 'Skupiny',
                    srovnani: 'Dříve/nyní',
                };
                Object.keys(labels).forEach((key) => {
                    const text = (this.comments[key] || '').trim();
                    if (text) {
                        lines.push(`${labels[key]}: ${text}`);
                    }
                });
                if (lines.length === 2) {
                    lines.push('(Zatím žádné připomínky.)');
                }
                return lines.join('\n');
            },
        },
        created() {
            this.loadComments();
            this.initFactsChats();
            this.applyScenario('quick');
        },
        methods: {
            initFactsChats(): void {
                FACT_CHATS.forEach((c) => {
                    this.$set(this.factsChatEnabled, c.id, true);
                });
            },
            sourceTip(label: string): string {
                return SOURCE_TIPS[label] || '';
            },
            intentTip(intent: Intent): string {
                if (intent === 'section') {
                    return this.tipIntentSection;
                }
                if (intent === 'faq') {
                    return this.tipIntentFaq;
                }
                return this.tipIntentWeave;
            },
            groupSubtitleTips(groupId: string): {label: string; text: string}[] {
                if (groupId === 'g-related') {
                    return [{label: 'PAS', text: SOURCE_TIPS.PAS}];
                }
                if (groupId === 'g-questions') {
                    return [
                        {label: 'PAA', text: SOURCE_TIPS.PAA},
                        {label: 'LSI', text: SOURCE_TIPS.LSI},
                    ];
                }
                if (groupId === 'g-lsi') {
                    return [{label: 'LSI', text: SOURCE_TIPS.LSI}];
                }
                if (groupId === 'g-long') {
                    return [{label: 'Long-tail', text: this.tipLongTail}];
                }
                if (groupId === 'g-cite') {
                    return [{label: 'E-E-A-T', text: this.tipEeat}];
                }
                if (groupId === 'g-ac') {
                    return [{label: 'AC', text: SOURCE_TIPS.AC}];
                }
                return [];
            },
            expandGroupsForCustomize(): void {
                this.groupOpen = {
                    'g-related': true,
                    'g-questions': true,
                    'g-lsi': false,
                    'g-long': true,
                    'g-cite': true,
                    'g-facts': true,
                    'g-ac': false,
                };
            },
            buildImmediatePreviewLines(): string[] {
                const lines: string[] = ['Téma článku (H1): „spořicí účet“ — vždy v článku.'];
                ALL_ROWS.forEach((row) => {
                    if (!this.isLucyOn(row.id) || row.isMain) {
                        return;
                    }
                    const intent = this.intentForRow(row.id);
                    if (intent === 'section') {
                        lines.push(`Vlastní odstavec (H2): ${row.phrase}`);
                    } else if (intent === 'faq') {
                        lines.push(`Otázka (FAQ): ${row.phrase}`);
                    } else if (intent === 'weave' && row.kind === 'citation') {
                        lines.push(`Citace (E-E-A-T): ${row.phrase}`);
                    } else if (intent === 'weave') {
                        lines.push(`Doplnit do textu: ${row.phrase}`);
                    }
                });
                const chats = FACT_CHATS.filter((c) => this.factsChatOn(c.id)).map((c) => c.label);
                if (chats.length) {
                    lines.push(`Fakta zapracovaná z chatů: ${chats.join(', ')}.`);
                }
                return lines;
            },
            onGenerovatHned(): void {
                this.quickWritePath = 'immediate';
                this.immediatePreview = {lines: this.buildImmediatePreviewLines()};
                this.lastGenerateFlash = '';
            },
            onUpravitPredGenerovanim(): void {
                this.quickWritePath = 'customize-first';
                this.immediatePreview = null;
                this.lastGenerateFlash = '';
                this.expandGroupsForCustomize();
                this.$nextTick(() => {
                    const el = document.getElementById('g-related');
                    if (el) {
                        el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                });
            },
            factsChatOn(id: string): boolean {
                return this.factsChatEnabled[id] !== false;
            },
            setFactsChat(id: string, on: boolean): void {
                this.$set(this.factsChatEnabled, id, on);
            },
            rowById(id: string): DemoRow | undefined {
                return ALL_ROWS.find((r) => r.id === id);
            },
            defaultIntent(row: DemoRow): Intent {
                if (row.kind === 'question') {
                    return 'faq';
                }
                if (row.kind === 'citation' || row.kind === 'variant') {
                    return 'weave';
                }
                return 'section';
            },
            intentForRow(id: string): Intent {
                const row = this.rowById(id);
                if (!row) {
                    return 'section';
                }
                return this.lucyIntent[id] || this.defaultIntent(row);
            },
            rowAllowsIntent(row: DemoRow): boolean {
                return row.kind !== 'citation';
            },
            intentOptionsForRow(row: DemoRow): {value: Intent; label: string}[] {
                if (row.kind === 'citation') {
                    return [];
                }
                const opts: Intent[] = row.kind === 'question'
                    ? ['faq', 'section', 'weave']
                    : ['section', 'faq', 'weave'];
                return opts.map((v) => ({value: v, label: INTENT_LABELS[v]}));
            },
            rowCountsAsSlot(row: DemoRow): boolean {
                if (row.isMain) {
                    return false;
                }
                if (!this.isLucyOn(row.id)) {
                    return false;
                }
                const intent = this.intentForRow(row.id);
                return intent === 'section' || intent === 'faq';
            },
            rowSlotTag(row: DemoRow): string {
                if (!this.isLucyOn(row.id)) {
                    return '';
                }
                const intent = this.intentForRow(row.id);
                if (intent === 'section') {
                    return '1 odstavec (H2)';
                }
                if (intent === 'faq') {
                    return '1 odstavec (FAQ)';
                }
                return '';
            },
            isLucyOn(id: string): boolean {
                return !!this.lucySelected[id];
            },
            setIntent(id: string, intent: Intent): void {
                const row = this.rowById(id);
                if (!row || !this.isLucyOn(id)) {
                    return;
                }
                const oldIntent = this.intentForRow(id);
                const oldCounts = oldIntent === 'section' || oldIntent === 'faq';
                const newCounts = intent === 'section' || intent === 'faq';
                if (newCounts && !oldCounts && this.slotCount >= this.maxSlots) {
                    this.showSnack('Limit 20 odstavců — vypněte jiný odstavec, nebo z něj udělejte „Doplnit do textu“.');
                    return;
                }
                this.$set(this.lucyIntent, id, intent);
            },
            toggleLucy(row: DemoRow): void {
                const on = this.isLucyOn(row.id);
                if (on) {
                    this.$set(this.lucySelected, row.id, false);
                    return;
                }
                const intent = this.defaultIntent(row);
                if ((intent === 'section' || intent === 'faq') && this.slotCount >= this.maxSlots) {
                    this.showSnack('Limit 20 odstavců — vypněte jiný odstavec, nebo z něj udělejte „Doplnit do textu“.');
                    return;
                }
                this.$set(this.lucyIntent, row.id, intent);
                this.$set(this.lucySelected, row.id, true);
            },
            lucyTooltip(row: DemoRow): string {
                if (row.isMain) {
                    return 'Hlavní téma článku — vždy v H1, nepočítá se do 20.';
                }
                if (row.kind === 'citation') {
                    return 'Outbound citace pro E-E-A-T — nepočítá se do 20 odstavců.';
                }
                if (!this.rowAllowsIntent(row) && this.isLucyOn(row.id)) {
                    return 'Citace zapracovaná do textu.';
                }
                if (this.isLucyOn(row.id)) {
                    return 'Vypnout — vždy lze, i nad limitem 20.';
                }
                const intent = this.defaultIntent(row);
                if ((intent === 'section' || intent === 'faq') && this.slotCount >= this.maxSlots) {
                    return 'Nelze zapnout jako nový odstavec — nejdřív uvolněte slot pod 20.';
                }
                return 'Zapnout pro AI Lucy.';
            },
            toggleGroup(id: string): void {
                this.$set(this.groupOpen, id, !this.groupOpen[id]);
            },
            groupCountLabel(group: DemoGroup): string {
                if (group.id === 'g-facts') {
                    return String(FACT_CHATS.length);
                }
                const n = group.items.length;
                return n > 10 ? `${n} · scroll 10` : String(n);
            },
            onScenarioChange(): void {
                this.applyScenario(this.scenario);
            },
            applyQuickBase(next: Record<string, boolean>, intents: Record<string, Intent>): void {
                QUICK_TOPICS.forEach((id) => {
                    next[id] = true;
                    intents[id] = 'section';
                });
                QUICK_QUESTIONS.forEach((id) => {
                    next[id] = true;
                    intents[id] = 'faq';
                });
                QUICK_CITATIONS.forEach((id) => {
                    next[id] = true;
                    intents[id] = 'weave';
                });
            },
            applyScenario(name: 'quick' | 'customize' | 'over-limit'): void {
                const next: Record<string, boolean> = {};
                const intents: Record<string, Intent> = {};
                this.initFactsChats();
                this.lastGenerateFlash = '';

                const collapsed = {
                    'g-related': false,
                    'g-questions': false,
                    'g-lsi': false,
                    'g-long': false,
                    'g-cite': false,
                    'g-facts': false,
                    'g-ac': false,
                };

                if (name === 'quick') {
                    this.quickWritePath = null;
                    this.immediatePreview = null;
                    this.applyQuickBase(next, intents);
                    this.lucySelected = next;
                    this.lucyIntent = intents;
                    this.groupOpen = collapsed;
                    return;
                }

                if (name === 'customize') {
                    this.applyQuickBase(next, intents);
                    intents['r-3'] = 'weave';
                    next['lt-1'] = true;
                    intents['lt-1'] = 'section';
                    next['q-5'] = false;
                    this.lucySelected = next;
                    this.lucyIntent = intents;
                    this.groupOpen = {
                        ...collapsed,
                        'g-related': true,
                        'g-questions': true,
                        'g-long': true,
                        'g-facts': true,
                    };
                    return;
                }

                // over-limit: old “select everything” as H2/FAQ
                ALL_ROWS.forEach((row) => {
                    if (row.isMain) {
                        return;
                    }
                    if (row.kind === 'citation') {
                        next[row.id] = true;
                        intents[row.id] = 'weave';
                        return;
                    }
                    if (row.groupId === 'g-related' || row.groupId === 'g-questions'
                        || row.groupId === 'g-lsi' || row.groupId === 'g-ac') {
                        next[row.id] = true;
                        intents[row.id] = row.kind === 'question' ? 'faq' : 'section';
                    }
                });
                this.lucySelected = next;
                this.lucyIntent = intents;
                this.groupOpen = {
                    ...collapsed,
                    'g-related': true,
                    'g-questions': true,
                    'g-lsi': true,
                };
            },
            onGenerovatClick(): void {
                if (this.generateDisabledReason) {
                    return;
                }
                const preview = this.buildImmediatePreviewLines();
                this.lastGenerateFlash = `Návrh: Generovat by spustilo psaní článku (${this.slotCount} odstavců v limitu). ${preview.slice(1, 4).join(' · ')}${preview.length > 4 ? '…' : ''} (demo — nic se nevolá).`;
            },
            showSnack(text: string): void {
                this.snackbarText = text;
                this.snackbar = true;
            },
            setComment(key: string, value: string): void {
                this.$set(this.comments, key, value);
                this.persistComments();
            },
            loadComments(): void {
                try {
                    const raw = localStorage.getItem(STORAGE_KEY);
                    if (raw) {
                        this.comments = JSON.parse(raw) as Record<string, string>;
                    }
                } catch {
                    this.comments = {};
                }
            },
            persistComments(): void {
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.comments));
                } catch {
                    /* ignore */
                }
            },
            copyCommentsSummary(): void {
                const text = this.commentsSummary;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(text).then(() => {
                        this.showSnack('Souhrn zkopírován.');
                    }).catch(() => {
                        this.showSnack('Kopírování se nepovedlo — použijte výběr z pole níže.');
                    });
                } else {
                    this.showSnack('Zkopírujte text z pole Souhrn připomínek.');
                }
            },
        },
    });
</script>

<style scoped lang="sass">
.navrh-vyber-main
    background: #eef1f8

.navrh-vyber-container
    max-width: 1100px

.navrh-eyebrow
    font-size: 11px
    letter-spacing: .14em
    text-transform: uppercase
    color: #0887dc
    font-weight: 700

.navrh-lead
    font-size: 16px
    color: #4a5368
    max-width: 820px

.navrh-card
    border-radius: 12px !important

.navrh-toolbar .navrh-seg
    border-radius: 999px
    overflow: hidden

.navrh-mock-wrap--mobile
    max-width: 390px
    margin: 0 auto

.navrh-app-shell
    background: #fff
    border-radius: 16px
    overflow: hidden
    border: 1px solid rgba(58, 53, 65, .12)
    box-shadow: 0 16px 40px rgba(15, 23, 42, .12)

.navrh-app-bar-inner
    background: linear-gradient(120deg, #0c98f5 0%, #0566c3 100%)
    color: #fff
    padding: 8px 14px
    font-size: 13px
    font-weight: 700
    display: flex
    align-items: center
    gap: 10px

.navrh-bar-pill
    background: rgba(255, 255, 255, .16)
    border-radius: 999px
    padding: 4px 10px
    font-size: 11px

.navrh-workspace
    padding: 16px 18px 24px
    background: #eef1f8

.navrh-fast-summary
    border-color: rgba(110, 170, 52, .45) !important
    background: #f8fcf3

.navrh-quick-fork
    border-color: rgba(8, 135, 220, .35) !important
    background: #fff

.navrh-fork-col
    height: 100%

.navrh-fork-line
    min-height: 36px

.navrh-immediate-preview
    border-color: rgba(76, 175, 80, .45) !important

.navrh-preview-list
    font-size: 13px
    line-height: 1.45

.navrh-group-subtips
    line-height: 1.6

.navrh-brief-strip
    background: #fff
    border-radius: 12px
    padding: 12px 14px
    border: 1px solid rgba(110, 170, 52, .35)

.navrh-lucy-avatar
    width: 28px
    height: 28px
    border-radius: 50%
    background: linear-gradient(135deg, #cd7ec2, #8e3d82)
    color: #fff
    display: inline-flex
    align-items: center
    justify-content: center
    font-weight: 800
    font-size: 13px
    margin-right: 10px
    flex-shrink: 0

.navrh-generovat
    font-weight: 700 !important

.navrh-counter-bar
    display: flex
    align-items: center
    justify-content: space-between
    flex-wrap: wrap
    gap: 8px
    background: #fff
    border-radius: 10px
    padding: 10px 12px
    border: 1px dashed #cfd8e6

.navrh-col-head
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    padding: 6px 10px
    font-size: 10px
    font-weight: 800
    text-transform: uppercase
    color: rgba(255, 255, 255, .85)
    background: linear-gradient(120deg, #0c98f5 0%, #0566c3 100%)

.navrh-scroll-10
    max-height: 430px
    overflow-y: auto

.navrh-row
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    align-items: start
    padding: 8px 10px
    border-bottom: 1px solid #edf0f5
    background: #fff

    &:hover
        background: #f7fbff

.navrh-row--main
    background: #333
    color: #fff

    .navrh-vol,
    .navrh-slot-tag
        color: rgba(255, 255, 255, .82)

.navrh-row__phrase
    font-size: 13px
    font-weight: 500
    line-height: 1.35

.navrh-row__meta
    display: flex
    flex-wrap: wrap
    gap: 4px
    margin-top: 4px
    align-items: center

.navrh-row__controls
    display: flex
    flex-direction: column
    align-items: flex-end
    min-width: 0

.navrh-src
    display: inline-flex
    align-items: center
    height: 18px
    padding: 0 6px
    border-radius: 999px
    font-size: 10px
    font-weight: 800
    border: 1px solid #d5dce8
    color: #4a5368
    background: #fff

.navrh-src--paa
    color: #0566c3
    border-color: #9ec9ee

.navrh-src--chat
    color: #8e3d82
    border-color: #e4b7dd
    background: #fbf4fa

.navrh-src--lsi
    color: #8e3d82
    border-color: #cd7ec2
    background: #f6e8f3

.navrh-src--pas
    color: #0566c3
    border-color: #b7d4c8
    background: #eef7f2

.navrh-src--ac
    color: #445
    border-color: #c9d4c0
    background: #f4f8ee

.navrh-src--top,
.navrh-src--labs
    color: #445
    border-color: #cfd6e4

.navrh-vol
    font-size: 11px
    color: #6b7385

.navrh-slot-tag
    font-size: 10px
    font-weight: 700
    color: #0566c3

.navrh-slot-tag--free
    color: #6b7385

.navrh-lucy-btn
    height: 26px !important
    min-width: 108px
    padding: 0 8px !important
    border-radius: 4px !important
    font-size: 11px !important
    font-weight: 700 !important

.navrh-lucy-btn--off
    background: #fff !important
    color: #8e3d82 !important
    box-shadow: inset 0 0 0 1px #cd7ec2 !important

.navrh-lucy-btn--on
    background: #6eaa34 !important
    color: #fff !important
    box-shadow: none !important

.navrh-intent-toggle
    flex-wrap: wrap
    justify-content: flex-end
    max-width: 280px

    .v-btn
        font-size: 10px !important
        letter-spacing: 0
        padding: 0 6px !important
        min-width: 0 !important

.navrh-main-chip
    font-weight: 700

.navrh-fact-chat
    margin: 8px 10px 12px
    padding: 10px 12px
    background: #f9fafc
    border-radius: 8px
    border: 1px solid #e4e9f2

.navrh-fact-chat__head
    display: flex
    align-items: center
    justify-content: space-between
    flex-wrap: wrap
    gap: 8px
    margin-bottom: 6px

.navrh-fact-list
    margin: 0
    padding-left: 18px
    font-size: 13px
    color: #4a5368

    li
        margin: 4px 0

.navrh-fact-switch
    flex: 0 0 auto

    ::v-deep .v-label
        font-size: 12px
        font-weight: 600

.navrh-summary
    white-space: pre-wrap
    font-size: 13px
    background: #f4f6fb
    padding: 12px
    border-radius: 8px
    max-height: 240px
    overflow: auto

.navrh-decisions li
    margin: 8px 0

.navrh-flash
    font-size: 14px
    font-weight: 500

@media (max-width: 600px)
    .navrh-row
        grid-template-columns: 1fr

    .navrh-row__controls
        align-items: flex-start
</style>
